/*
 * Sample contacts (P5.2, P6.5): the people at customers and prospects —
 * who signs, who decides, who runs the system day to day — and how strong
 * our relationship with each of them is.
 *
 * Phone and email are protected fields (Settings → Field Security): roles
 * without "view contact details" see them masked.
 */
import { daysFromNow } from "@/helpers/crm";
import { customers } from "@/mock/customers";

// New master lists for Settings → Lookups (contact role type, relationship strength).
export const contactRoles = [
  { key: "primary", name: "Primary contact" },
  { key: "decision_maker", name: "Decision maker" },
  { key: "technical", name: "Technical contact" },
  { key: "finance", name: "Finance / billing" },
  { key: "champion", name: "Champion / key user" },
  { key: "user", name: "End user" },
];
export const relationshipStrengths = [
  { key: "strong", name: "Strong", tone: "success", score: 4 },
  { key: "good", name: "Good", tone: "info", score: 3 },
  { key: "weak", name: "Weak", tone: "warning", score: 2 },
  { key: "none", name: "No relationship", tone: "danger", score: 1 },
];

// Extra people per customer: [name, title, role, decision maker?, strength, last engaged days ago]
const PEOPLE = {
  1: [["Robert Kiprono", "Group CFO", "decision_maker", true, "good", 21], ["Wilfred Onyango", "IT Manager", "technical", false, "strong", 4]],
  2: [["Agnes Nyambura", "CEO", "decision_maker", true, "good", 40], ["Felix Omondi", "ICT Officer", "technical", false, "strong", 6]],
  3: [["Salim Bakari", "Managing Director", "decision_maker", true, "weak", 75], ["Joyce Wekesa", "Payroll Officer", "user", false, "weak", 30]],
  4: [["Dr. Samuel Owino", "Director", "decision_maker", true, "none", 120], ["Brian Odera", "Systems Admin", "technical", false, "good", 12]],
  5: [["Charles Macharia", "Finance Director", "decision_maker", true, "good", 18], ["Eunice Wangui", "Accountant", "finance", false, "good", 9]],
  6: [["Dr. Ruth Muthoni", "Medical Director", "decision_maker", true, "weak", 50], ["Ian Kibet", "IT Officer", "technical", false, "good", 14]],
  7: [["Sr. Margaret Akoth", "School Manager", "decision_maker", true, "good", 10], ["Collins Mutai", "ICT Teacher", "technical", false, "strong", 2]],
  8: [["Patrick Cheruiyot", "Managing Director", "decision_maker", true, "good", 8], ["Naomi Jepkosgei", "HR Manager", "champion", false, "strong", 1], ["Elijah Sang", "IT Lead", "technical", false, "good", 3]],
  9: [["Esther Chepkemoi", "Board Chair", "decision_maker", true, "good", 60], ["David Muriuki", "Head of ICT", "technical", false, "strong", 5], ["Lydia Kerubo", "Finance Manager", "finance", false, "good", 11]],
  10: [["Nelly Atieno", "Finance Lead", "finance", false, "good", 20]],
  11: [["Grace Nakato", "Finance Director", "decision_maker", true, "good", 6], ["Ronald Okello", "IT Support", "technical", false, "good", 2]],
  12: [["Peter Ndungu", "Country Director", "decision_maker", true, "weak", 25]],
  13: [["Jacqueline Mumbi", "HR Manager", "champion", false, "strong", 7], ["Henry Mugo", "CFO", "decision_maker", true, "good", 33]],
  14: [["Omar Abdalla", "Owner", "decision_maker", true, "none", 70]],
  15: [["Rev. Isaac Gitonga", "Board Secretary", "decision_maker", true, "good", 45]],
  16: [["Mary Kendi", "HR Officer", "user", false, "weak", 38]],
  17: [],
  18: [["Abdi Mohamed", "CEO", "decision_maker", true, "strong", 15], ["Rehema Mwinyi", "IT Manager", "technical", false, "strong", 3], ["Kassim Omar", "Payroll Lead", "champion", false, "good", 9]],
};

// Job titles of the primary contacts on the customer records.
const PRIMARY_TITLE = {
  1: "Group HR Manager", 2: "COO", 3: "HR Officer", 4: "Operations Manager", 5: "HR Manager", 6: "Administrator",
  7: "Principal", 8: "Finance Director", 9: "CEO", 10: "Owner", 11: "Operations Manager", 12: "Programs Lead",
  13: "HR Manager", 14: "General Manager", 15: "Bursar", 16: "CFO", 17: "Owner", 18: "HR Director",
};

const phoneOf = (n) => `+254 7${String(20 + (n % 70)).padStart(2, "0")} ${String(400 + n * 11).slice(-3)} ${String(200 + n * 17).slice(-3)}`;
const emailOf = (name, org) => {
  const parts = name.replace(/^(Dr\.|Rev\.|Sr\.)\s/, "").toLowerCase().split(" ");
  return `${parts[0][0]}.${parts.slice(-1)[0]}@${org.split(" ")[0].toLowerCase()}.co.ke`;
};

let id = 0;
const contact = (org, extra) => {
  id += 1;
  return { id, phone: phoneOf(id), email: emailOf(extra.name, org), ...extra };
};

export const contacts = [];
customers.forEach((c) => {
  // The primary contact on the customer record is always here, flagged primary.
  contacts.push(
    contact(c.name, {
      customer_id: c.id, prospect_id: null, organisation: c.name, name: c.primary_contact, title: PRIMARY_TITLE[c.id] ?? "Primary contact",
      role: "primary", decision_maker: false, strength: c.health === "critical" ? "weak" : "strong",
      last_engaged: daysFromNow(-(c.lifecycle === "former" ? 65 : (c.id % 9) + 1)), owner_id: c.csm_id, status: c.lifecycle === "former" ? "inactive" : "active",
    }),
  );
  (PEOPLE[c.id] ?? []).forEach(([name, title, role, dm, strength, ago]) =>
    contacts.push(
      contact(c.name, {
        customer_id: c.id, prospect_id: null, organisation: c.name, name, title, role, decision_maker: dm, strength,
        last_engaged: daysFromNow(-ago), owner_id: dm ? c.account_manager_id : c.csm_id, status: c.lifecycle === "former" ? "inactive" : "active",
      }),
    ),
  );
});

// Prospect-side contacts, so the list covers the whole journey.
[
  [1, "Kilimo Fresh Exporters", "Mary Atieno", "Finance Manager", "primary", true, "good", 3, 1],
  [4, "Lakeview SACCO", "Rose Akinyi", "CEO", "decision_maker", true, "weak", 9, 4],
  [12, "Thika Steel Works", "George Kimani", "HR Manager", "primary", false, "none", 30, null],
].forEach(([pid, org, name, title, role, dm, strength, ago, owner]) =>
  contacts.push(contact(org, { customer_id: null, prospect_id: pid, organisation: org, name, title, role, decision_maker: dm, strength, last_engaged: daysFromNow(-ago), owner_id: owner, status: "active" })),
);
// Daniel Rotich signed for Rift Valley Millers: primary and decision maker in one.
contacts.find((k) => k.customer_id === 8 && k.role === "primary").decision_maker = true;

export const contactsFor = (customerId) => contacts.filter((k) => k.customer_id === Number(customerId));
export const contactRole = (key) => contactRoles.find((r) => r.key === key)?.name ?? key;
export const strengthOf = (key) => relationshipStrengths.find((s) => s.key === key) ?? relationshipStrengths[3];

// What a role without contact-detail rights sees.
export const maskPhone = (p) => (p ? `${p.slice(0, 7)}*** ***` : "—");
export const maskEmail = (e) => (e ? `${e[0]}*****@${e.split("@")[1]}` : "—");
